import type { TemplateResumeSummary } from '../../lib/resumeTemplates'
import { Button } from './Button'
import { useDialogKeyboardShortcuts } from '../hooks/useDialogKeyboardShortcuts'

type CreateResumeModalProps = {
  isOpen: boolean
  templateResumes: TemplateResumeSummary[]
  creating: boolean
  onClose: () => void
  onCreateBlank: () => void
  onCreateFromTemplate: (templateId: string) => void
}

function formatTemplateDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function CreateResumeModal({
  isOpen,
  templateResumes,
  creating,
  onClose,
  onCreateBlank,
  onCreateFromTemplate,
}: CreateResumeModalProps) {
  useDialogKeyboardShortcuts({
    isOpen,
    onCancel: onClose,
  })

  if (!isOpen) return null

  const hasTemplates = templateResumes.length > 0

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-overlay transition-opacity duration-normal"
        onClick={creating ? undefined : onClose}
        role="presentation"
        aria-hidden="true"
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-resume-title"
          className="relative bg-surface-default rounded-lg shadow-md max-w-lg w-full transform transition-all duration-normal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5">
            <h2 id="create-resume-title" className="text-h3 text-text-primary font-semibold mb-2">
              New resume
            </h2>
            <p className="text-small text-text-secondary mb-5">
              Start blank to tailor from scratch (all skills off by default), or copy a template
              resume with all sections and skill checkmarks.
            </p>

            <div className="space-y-3">
              <button
                type="button"
                disabled={creating}
                onClick={onCreateBlank}
                aria-label="Blank resume"
                className="w-full text-left rounded-lg border border-border-default px-4 py-3 hover:border-interactive hover:bg-surface-subtle transition-colors disabled:opacity-50"
              >
                <span className="block text-body font-medium text-text-primary">Blank resume</span>
                <span className="block text-small text-text-secondary mt-1">
                  Contact seed and account catalog; choose skills on the Skills tab.
                </span>
              </button>

              <div
                className={`rounded-lg border border-border-default px-4 py-3 ${
                  hasTemplates ? '' : 'opacity-60'
                }`}
              >
                <span className="block text-body font-medium text-text-primary">From template</span>
                <span className="block text-small text-text-secondary mt-1 mb-3">
                  {hasTemplates
                    ? 'Full copy of sections and included skills from a template resume.'
                    : 'Mark an existing resume as template first.'}
                </span>

                {hasTemplates && (
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    {templateResumes.map((template) => (
                      <li key={template.id}>
                        <button
                          type="button"
                          disabled={creating}
                          onClick={() => onCreateFromTemplate(template.id)}
                          className="w-full text-left rounded-md border border-border-strong px-3 py-2 text-small hover:border-interactive hover:bg-surface-subtle transition-colors disabled:opacity-50"
                        >
                          <span className="font-medium text-text-primary">{template.title}</span>
                          <span className="block text-text-muted mt-0.5">
                            Updated {formatTemplateDate(template.updatedAt)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button type="button" variant="outline" onClick={onClose} disabled={creating}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
